import { ChordLib, getMultiValuedSuffix } from '@tunasong/music-lib'
import type { AnswerVerdict, QuizQuestion } from '@tunasong/music-lib'
import type { FC } from 'react'
import { useTheoryCopy } from '../theory-copy/use-theory-copy.js'
import styles from './function-quiz-feedback.module.css'

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

const scaleLabel = (question: QuizQuestion): string => `${question.scale.root} ${capitalize(question.scale.type ?? 'major')}`

export interface FunctionQuizFeedbackProps {
  question: QuizQuestion
  verdict: AnswerVerdict
  onHearAgain: () => void
  onNext: () => void
}

/**
 * Function Quiz v1 post-answer reveal. Renders:
 *   - Verdict header ("Correct." / "Not quite.") in success/error colour
 *   - Roman-numeral identification + level-resolved lexicon body for the
 *     chord function (e.g. function-ii intermediate copy)
 *   - For multi-valued chords (major iii, major vi, minor ♭VI — Bowie §8.1),
 *     an additional sentence naming both roles. Sourced from the analyzer's
 *     `getMultiValuedSuffix` so the wording is locked next to the pool data
 *     instead of duplicated in the UI layer.
 *   - "Hear it again" + "Next ›" actions. Next is manual per Bowie §Q2
 *     verdict (no auto-advance).
 */
export const FunctionQuizFeedback: FC<FunctionQuizFeedbackProps> = props => {
  const { question, verdict, onHearAgain, onNext } = props
  const lexicon = useTheoryCopy(question.entry.lexiconId)
  const chordName = ChordLib.getName(question.chord)
  const header = verdict.correct ? 'Correct.' : 'Not quite.'
  const lexBody = lexicon?.body ?? ''
  const multiSuffix = getMultiValuedSuffix(verdict.entry)
  return (
    <section className={styles.feedback} role="status" aria-live="polite">
      <p className={`${styles.header} ${verdict.correct ? styles.correct : styles.incorrect}`}>{header}</p>
      <p className={styles.body}>
        {chordName} is {question.entry.roman} in {scaleLabel(question)}
        {lexBody ? ` — ${lexBody}` : ''}
      </p>
      {multiSuffix ? <p className={styles.body}>{multiSuffix}</p> : null}
      <div className={styles.actions}>
        <button type="button" onClick={onHearAgain}>
          ▶ Hear it again
        </button>
        <button type="button" className={styles.nextButton} onClick={onNext}>
          Next ›
        </button>
      </div>
    </section>
  )
}
